import { useEffect, useState } from "react";
import type { NewsletterConfirmation } from "../../api/types";
import { t } from "../../i18n";

interface Props {
  token: string;
}

/**
 * De nieuwsbrief bevestigen (Furkan, 30/10/2026): the customer's page behind the link in the mail Tafo sends when
 * staff switch his newsletter on, on any phone, without signing in. As the page behind "uitschrijven", opening it
 * changes nothing (a mail app may look at a link before its reader does): only the customer's press on the button
 * switches the newsletter on.
 */
export function NewsletterConfirmPage({ token }: Props) {
  const text = t.newsletterConfirm;
  const [link, setLink] = useState<NewsletterConfirmation | null>(null);
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const address = `/api/v1/newsletter/confirm/${encodeURIComponent(token)}`;

  useEffect(() => {
    let stopped = false;
    fetch(address)
      .then(async (response) => {
        if (stopped) return;
        if (response.ok) setLink((await response.json()) as NewsletterConfirmation);
        else setGone(true);
      })
      .catch(() => {
        if (!stopped) setFailed(true);
      });
    return () => {
      stopped = true;
    };
  }, [address]);

  useEffect(() => {
    document.title = link ? `${text.title} · ${link.shop}` : text.title;
  }, [link, text.title]);

  const confirm = () => {
    setBusy(true);
    setFailed(false);
    fetch(address, { method: "POST" })
      .then(async (response) => {
        if (response.ok) setLink((await response.json()) as NewsletterConfirmation);
        // lapsed or withdrawn in the meantime
        else if (response.status === 404) setGone(true);
        else setFailed(true);
      })
      .catch(() => {
        setFailed(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (gone) {
    return (
      <main className="trackpage">
        <p className="trackgone">{text.gone}</p>
      </main>
    );
  }
  if (!link) {
    return (
      <main className="trackpage" aria-busy={!failed}>
        {failed && <p className="trackgone">{text.failed}</p>}
      </main>
    );
  }

  return (
    <main className="trackpage">
      <h1>{text.title}</h1>
      <div className="newsletter">
        <b>{link.shop}</b>
        {link.confirmed ? (
          <>
            <p role="status">{text.confirmed}</p>
            <p className="note">{text.stop}</p>
          </>
        ) : (
          <>
            <p>{text.ask}</p>
            <button type="button" className="btn primary" disabled={busy} onClick={confirm}>
              {text.confirm}
            </button>
            <p className="note">{text.nothing}</p>
          </>
        )}
        {failed && <p className="pinmsg">{text.failed}</p>}
      </div>
    </main>
  );
}

/** The token when this page is the one behind the link in the mail (/nieuwsbrief/bevestigen/<token>), else null. */
export function newsletterConfirmToken(pathname: string): string | null {
  return /^\/nieuwsbrief\/bevestigen\/([A-Za-z0-9]{24})\/?$/.exec(pathname)?.[1] ?? null;
}
